/**
 * Stable stream / VNC-bridge failure codes for Desktop WebRTC renew.
 *
 * UI and reconnect logic must branch on these codes — never on free-form
 * English strings — so long-lived tabs can renew tickets or stop an
 * infinite "Connecting…" / negotiate spinner without a full page reload.
 */
export const StreamErrorCodes = {
    STREAM_MINT_FAILED: "STREAM_MINT_FAILED",
    STREAM_TOKEN_EXPIRED: "STREAM_TOKEN_EXPIRED",
    STREAM_TOKEN_REJECTED: "STREAM_TOKEN_REJECTED",
    BRIDGE_NEGOTIATE_FAILED: "BRIDGE_NEGOTIATE_FAILED",
    BRIDGE_NEGOTIATE_TIMEOUT: "BRIDGE_NEGOTIATE_TIMEOUT",
    STREAM_REFRESH_FAILED: "STREAM_REFRESH_FAILED",
};
export class StreamError extends Error {
    code;
    constructor(code, message, options) {
        super(message ?? code, options);
        this.name = "StreamError";
        this.code = code;
    }
}
export function isStreamError(err) {
    return err instanceof StreamError;
}
/** Expired / rejected tickets should mint a fresh stream-url and reconnect. */
export function shouldRefreshAndReconnect(code) {
    return (code === StreamErrorCodes.STREAM_TOKEN_EXPIRED ||
        code === StreamErrorCodes.STREAM_TOKEN_REJECTED);
}
/** Hard stop + manual retry UI (no infinite Connecting). */
export function shouldStopForManualRetry(code) {
    return (code === StreamErrorCodes.STREAM_MINT_FAILED ||
        code === StreamErrorCodes.BRIDGE_NEGOTIATE_FAILED ||
        code === StreamErrorCodes.BRIDGE_NEGOTIATE_TIMEOUT ||
        code === StreamErrorCodes.STREAM_REFRESH_FAILED);
}
export function classifyStreamError(err) {
    if (err instanceof StreamError)
        return err;
    const msg = err instanceof Error ? err.message : String(err ?? "unknown stream error");
    const lower = msg.toLowerCase();
    if (/jwt expired|token expired|credentials? expired|expired token|expir(ed|y)/i.test(lower) ||
        /connack.*\b5\b|not authorized.*expir/i.test(lower)) {
        return new StreamError(StreamErrorCodes.STREAM_TOKEN_EXPIRED, msg, { cause: err });
    }
    if (/token.*(invalid|reject|revoked)|not authorized|bad user|authentication|connack.*\b4\b|unauthorized|forbidden/i.test(lower)) {
        return new StreamError(StreamErrorCodes.STREAM_TOKEN_REJECTED, msg, { cause: err });
    }
    if (/answer timeout|negotiate(?:\s|_|-)?timeout|bridge.*timeout/i.test(lower)) {
        return new StreamError(StreamErrorCodes.BRIDGE_NEGOTIATE_TIMEOUT, msg, { cause: err });
    }
    if (/vnc-bridge|negotiate|offer|answer|signaling|ice gather|no m-lines/i.test(lower)) {
        return new StreamError(StreamErrorCodes.BRIDGE_NEGOTIATE_FAILED, msg, { cause: err });
    }
    if (/stream-url|mint|failed to fetch stream|http\/? ?[45]\d\d|\b404\b|\b500\b|\b502\b|\b503\b/i.test(lower)) {
        return new StreamError(StreamErrorCodes.STREAM_MINT_FAILED, msg, { cause: err });
    }
    if (/refresh/i.test(lower)) {
        return new StreamError(StreamErrorCodes.STREAM_REFRESH_FAILED, msg, { cause: err });
    }
    return new StreamError(StreamErrorCodes.STREAM_REFRESH_FAILED, msg, { cause: err });
}
export function streamErrorLabel(code) {
    switch (code) {
        case StreamErrorCodes.STREAM_MINT_FAILED:
            return "Failed to get stream credentials";
        case StreamErrorCodes.STREAM_TOKEN_EXPIRED:
            return "Stream ticket expired";
        case StreamErrorCodes.STREAM_TOKEN_REJECTED:
            return "Stream ticket rejected";
        case StreamErrorCodes.BRIDGE_NEGOTIATE_FAILED:
            return "Failed to negotiate with vnc-bridge";
        case StreamErrorCodes.BRIDGE_NEGOTIATE_TIMEOUT:
            return "Timed out negotiating with vnc-bridge";
        case StreamErrorCodes.STREAM_REFRESH_FAILED:
            return "Failed to refresh stream credentials";
        default:
            return "Stream connection failed";
    }
}
